import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, ExternalLink, MessageSquare } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { studentNav } from "../../components/shared/navConfig";
import { toast } from "sonner";

const STATUS_ALIAS = {
  Submitted: "Applied",
  "Under Review": "Under Review",
  Shortlisted: "Shortlisted",
  Rejected: "Rejected",
  Allocated: "Allocated",
};
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

export default function StudentApplications() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/applications/mine"); setRows(data); }
      catch { toast.error("Failed to load"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = rows.filter(r => !q || (r.task?.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardShell nav={studentNav} title="My applications" subtitle="Everything you have applied to">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border flex-1 max-w-md" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            <Search size={13} className="text-zinc-500"/>
            <input data-testid="apps-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 flex-1"/>
          </div>
          <div className="text-xs text-zinc-500">{filtered.length} record{filtered.length !== 1 && "s"}</div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500">
            <FileText size={22} className="mx-auto mb-3 text-zinc-600"/>
            You haven't applied to any tasks yet.
            <div className="mt-4">
              <Link to="/student/tasks" className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">Browse tasks <ExternalLink size={12}/></Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(a => (
              <div key={a.application_id} data-testid={`app-row-${a.application_id}`} className="p-4 rounded-md border flex items-center gap-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center font-mono text-[10px] text-blue-400"
                     style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
                  {a.match_score}%
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{a.task?.title || "—"}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {a.task?.company_name} · <span className="font-mono">{a.task_id}</span> · applied {fmt(a.applied_on)}
                  </div>
                </div>
                <div className="text-xs text-zinc-400 hidden md:block">₹{Number(a.task?.budget || 0).toLocaleString()}</div>
                <StatusBadge status={STATUS_ALIAS[a.status] || a.status}/>
                {a.status === "Allocated" && (
                  <Link to="/workroom" className="btn-primary px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5">
                    <MessageSquare size={12}/> Open workroom
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
